import _ from "lodash";
import { useMemo } from "react";
import { useDataMap } from "../../common/hooks/api/useDataMap";
import { NetworkRequestState } from "../../common/models";
import {
  disclosedPICGroupsFromFilteredDisclosed,
  useDataMapCollected,
  useDataMapCollectedPICGroups,
  useDataMapDisclosed,
} from "../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../common/service/server/dto/CollectionGroupDto";
import {
  DataMapDto,
  DisclosureDto,
  PICGroup,
} from "../../common/service/server/dto/datamap/DataMapDto";
import { DataMapOperation } from "../../common/service/server/dto/datamap/DataMapRequestDto";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";
import { alphabetically } from "../surveys/util";
import { useOrganizationVendors } from "./useOrganizationVendors";

export type UpdateDisclosedArgs = {
  vendorId: UUIDString;
  disclosedGroupIds: UUIDString[];
  disclosed: boolean;
};

export type UseDisclosuresForCollectionGroups = {
  requests: { recipientsRequest: NetworkRequestState; dataMapRequest: NetworkRequestState };
  actions: {
    updateDisclosure: (args: UpdateDisclosedArgs) => void;
    refreshVendors: () => Promise<void>;
  };
  dataMap: DataMapDto | undefined;
  dataRecipients: OrganizationDataRecipientDto[];
  dataRecipientsWithDisclosedPIC: OrganizationDataRecipientDto[];
  disclosed: DisclosureDto[];
  collectedPICGroups: PICGroup[];
  unmappedDisclosurePICGroups: PICGroup[];
  complete: boolean;
  pendingComplete: boolean;
};

export const useDisclosuresForCollectionGroups = (
  orgId: OrganizationPublicId,
  collectionGroups: CollectionGroupDetailsDto[],
  collectionContext: CollectionContext[],
  vendorFilter?: (vendor: OrganizationDataRecipientDto, dataMap: DataMapDto) => boolean,
): UseDisclosuresForCollectionGroups => {
  const { data: dataMap, request: dataMapRequest, mutators } = useDataMap(orgId);
  const { patch: patchDataMap } = mutators;

  const [allRecipients, recipientsRequest, refreshVendors] = useOrganizationVendors(
    orgId,
    undefined,
    true,
  );

  let recipients = allRecipients;
  if (vendorFilter) {
    recipients = allRecipients.filter((vendor) => vendorFilter(vendor, dataMap));
  }

  const relevantGroupIds = useMemo(
    () => collectionGroups.filter((g) => Boolean(g)).map((g) => g.id),
    [collectionGroups],
  );

  const dataRecipientsWithDisclosedPIC = useMemo(() => {
    const associations = dataMap.recipientAssociations.filter((a) =>
      relevantGroupIds.includes(a.groupId),
    );
    const associatedVendors = new Set(associations.map((a) => a.vendorId));
    return (
      recipients
        ?.filter(
          (r) =>
            associatedVendors.has(r.vendorId) &&
            _.intersection(r.collectionContext, collectionContext).length > 0,
        )
        ?.sort(alphabetically) ?? []
    );
  }, [recipients, collectionContext, dataMap, relevantGroupIds]);

  const contextualRecipients = useMemo(() => {
    return (
      recipients
        ?.filter((r) => _.intersection(r.collectionContext, collectionContext).length > 0)
        ?.sort(alphabetically) ?? []
    );
  }, [recipients, collectionContext]);

  const collection = useDataMapCollected(dataMap, { collectionGroupId: relevantGroupIds });
  const disclosed = useDataMapDisclosed(dataMap, { collectionGroupId: relevantGroupIds });
  const collectedPICGroups = useDataMapCollectedPICGroups(dataMap, {
    collectionGroupId: relevantGroupIds,
  });

  const updateDisclosure = ({ vendorId, disclosedGroupIds, disclosed }: UpdateDisclosedArgs) => {
    const operations: DataMapOperation[] = disclosedGroupIds.flatMap((picGroupId) => {
      // Find the Collection Group IDs for each relevant group who collects PIC within this PIC Group
      const allCollectionGroupIds = collection
        .filter((c) => dataMap?.categories[c.picId]?.picGroupId == picGroupId)
        .map((c) => c.groupId);
      const collectionGroupIds = _.uniq(allCollectionGroupIds);

      return collectionGroupIds.map((groupId) => {
        return {
          target: "DISCLOSURE",
          action: disclosed ? "ADD" : "REMOVE",
          groupId,
          vendorId,
          picGroupId,
        };
      });
    });

    patchDataMap.mutate({ operations });
  };

  const complete = useMemo(() => {
    return dataRecipientsWithDisclosedPIC.every((dr) =>
      disclosed.some((d) => d.vendorId == dr.vendorId),
    );
  }, [dataRecipientsWithDisclosedPIC, disclosed]);

  // Calculate all disclosed groups and unselected groups
  const unmappedDisclosurePICGroups = useMemo(() => {
    // Get all disclosed groups for each recipient
    const recipientDisclosures = contextualRecipients.map((recipient) =>
      disclosedPICGroupsFromFilteredDisclosed(dataMap, {
        vendorId: recipient.vendorId,
        collectionGroupId: relevantGroupIds,
      }),
    );

    // Get unique selected group IDs across all recipients
    const selectedGroupIds = _.uniq(recipientDisclosures.flat().map((group) => group.id));

    // Find groups that aren't selected by any recipient
    const unselectedGroups = collectedPICGroups.filter(
      (group) => !selectedGroupIds.includes(group.id),
    );

    return unselectedGroups;
  }, [contextualRecipients, dataMap, relevantGroupIds, collectedPICGroups]);

  const pendingComplete = useMemo(() => {
    const allVendorsHaveDisclosures = contextualRecipients.every((dr) =>
      disclosed.some((d) => d.vendorId == dr.vendorId),
    );

    return allVendorsHaveDisclosures && unmappedDisclosurePICGroups.length === 0;
  }, [contextualRecipients, disclosed, unmappedDisclosurePICGroups]);

  return {
    requests: {
      recipientsRequest,
      dataMapRequest,
    },
    actions: { updateDisclosure, refreshVendors },
    dataMap,
    dataRecipients: contextualRecipients,
    dataRecipientsWithDisclosedPIC,
    disclosed,
    collectedPICGroups,
    unmappedDisclosurePICGroups,
    complete,
    pendingComplete,
  };
};
